/* eslint-disable no-unused-expressions, no-undef, consistent-return, no-shadow, camelcase */
import React from 'react';

import { Cookie } from 'next-cookie';
import Image from 'next/image';
import { useRouter } from 'next/router';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';

/* eslint-disable import/extensions,import/no-unresolved */
import NotificationBell from '@/components/NotificationBell';
/* eslint-enable import/extensions,import/no-unresolved */

import useRazorpay from '../../../hooks/useRazorpay';
import { createRazorpayOrder, resetPaymentProcess } from '../../../redux/slices/checkoutSlice';
import { EVENT_ATTRIBUTE, useMoengageEventTracker } from '../../../hooks/useMoengage';
import useSourceScreen from '../../../hooks/useSourceScreen';
import { courseExpireDate, fetchCourseSlugData, openInAppCheck } from '../../../utils/helper';
import PaymentModal from '../../PaymentModal/PaymentModal';
import * as PaymentActions from '../../../utils/paymentConfig';

import { startPaymentProcess, startPaytmPaymentProcess } from '../../../redux/slices/cartSlice';
import {
  generateCourseEMIOrder,
  selectedEMI,
  showEmiModal,
  showPaymentModal,
} from '../../../redux/slices/librarySlice';
import { isAndroidModal } from '../../../redux/slices/courseConsumption';

import liveClassIcon from '../../../assets/images/course-dashboard/icons/LiveClassIcon.gif';

import BottomActionComponent from './BottomActionComponent';
import styles from './CourseCard.module.scss';
import CCAvenuePaymentService from '../../../service/payment/cc-avenue';

const cookies = new Cookie();

function CourseEMIModal({ course }) {
  const dispatch = useDispatch();

  const { courseEMIDetails, emiModal } = useSelector((state) => ({
    courseEMIDetails: state.library.courseEMIDetails,
    emiModal: state.library.emiModal?.show,
  }));

  React.useEffect(() => {
    if (emiModal) {
      document.body.classList.add('noscroll');
    }
    return () => {
      document.body.classList.remove('noscroll');
    };
  }, [emiModal]);

  const handleRepayment = (courseEMI) => (event) => {
    event.stopPropagation();
    dispatch(selectedEMI({ emiData: courseEMI }));
    dispatch(showPaymentModal({ show: true }));

    dispatch(showEmiModal({ show: false, courseId: course?.courseId }));
  };

  if (!courseEMIDetails?.courseEMIData?.data?.length) return null;

  const emiToBePaid = courseEMIDetails?.courseEMIData?.data?.findIndex(
    (emi) => emi?.paymentStatus?.toLowerCase() === 'unpaid',
  );

  const handleCloseModal = () => {
    dispatch(showEmiModal({ show: false }));
  };

  return (
    <>
      {/* {showEMI && ( */}
      <div className={classNames(styles.emiModal)}>
        <div className={styles.emiModalWrapper}>
          <div className={styles.modalHeader}>
            <div className={styles.modalTitle}>Course Validity</div>
            <span onClick={handleCloseModal} className={styles.closeIcon}>
              <FontAwesomeIcon icon={faXmark} />
            </span>
          </div>
          <div className={styles.modalBody}>
            <div className={styles.emiListGroup}>
              <span>Purchase Date :</span>
              <span>{new Date(courseEMIDetails?.courseEMIData?.courseStartDate).toLocaleDateString()}</span>
            </div>
            <div className={styles.emiListGroup}>
              <span>Valid Till :</span>
              <span>{new Date(courseEMIDetails?.courseEMIData?.courseValidityDate).toLocaleDateString()}</span>
            </div>
            {courseEMIDetails?.courseEMIData?.data?.map((courseEmi, index) => {
              const paymentStatus = courseEmi?.paymentStatus?.toLowerCase();
              return (
                <>
                  <div className={styles.emiInstalmnt}>
                    <div>
                      <div>{courseEmi.title}</div>
                      <div>
                        <span>Valid Till : {new Date(courseEmi?.validUpto)?.toLocaleDateString()}</span>
                      </div>
                    </div>
                    <div>
                      {'\u20B9'}
                      {courseEmi.emiAmount}
                    </div>
                    {paymentStatus === 'success' && <span className={styles.emiInstalmntBtn}>Paid</span>}
                    {paymentStatus === 'unpaid' && (
                      <span
                        onClick={handleRepayment(courseEmi)}
                        className={classNames(styles.emiInstalmntBtn, {
                          [styles.disabled]: index !== emiToBePaid,
                        })}
                      >
                        Pay Now
                      </span>
                    )}
                  </div>
                  {index === courseEMIDetails.courseEMIData.data.length - 1 &&
                    courseEMIDetails?.courseEMIData?.offerStatus === 1 && (
                      <div className={styles.emiOffer}>{courseEMIDetails?.courseEMIData?.offerMsg}</div>
                      // eslint-disable-next-line indent
                    )}
                </>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
}

function VideoCourse({
  course,
  isExpiring,
  isExpired,
  repayEmi,
  canStartCourse,
  paymentMethods,
  index,
  className: classNameValue,
}) {
  const router = useRouter();
  const dispatch = useDispatch();
  const trackMoengageEvent = useMoengageEventTracker();

  const new_course_consumption_enable = useSelector((state) => state.auth?.authUser?.newCourseConsumptionEnable);
  const screen = useSourceScreen();
  // Start Course
  const startCourse = async (event) => {
    const { courseTypeName: courseType } = course;
    if (!canStartCourse(course, event)) return false;
    const showAppModal = openInAppCheck();
    if (showAppModal) {
      dispatch(isAndroidModal({ show: true }));
      return false;
    }
    const redirectionFlag = new_course_consumption_enable;
    const courseID = course.courseId;
    const languageID = course.lanugageId;
    let newId;
    let newSlug;
    try {
      const { id, slug } = await fetchCourseSlugData(courseID, languageID);
      newId = id;
      newSlug = slug;
    } catch (error) {
      //
    } finally {
      if (redirectionFlag) {
        try {
          trackMoengageEvent('start_my_course', {
            [EVENT_ATTRIBUTE.COURSE_ID]: courseID,
            [EVENT_ATTRIBUTE.COURSE_NAME]: course?.courseTitle,
            [EVENT_ATTRIBUTE.COURSE_TYPE]: course?.freeCourse ? 'free' : 'paid',
            [EVENT_ATTRIBUTE.COURSE_STATUS]: course?.course_status,
            [EVENT_ATTRIBUTE.COURSE_CATEGORY]: course?.courseCategory,
            [EVENT_ATTRIBUTE.VALID_TILL]: course.validityDate,
            [EVENT_ATTRIBUTE.LANGUAGE]: languageID,
            // [EVENT_ATTRIBUTE.COURSE_CATEGORY]: courseType?.type?.toLowerCase(),
            [EVENT_ATTRIBUTE.SOURCE_SCREEN]: screen,
            [EVENT_ATTRIBUTE.EXPIRES_IN]: course?.validityDaysLeft >= 0 ? course?.validityDaysLeft : 'free',
            is_live: false,
          });
          trackMoengageEvent('course_dashboard_landed', {
            [EVENT_ATTRIBUTE.COURSE_CATEGORY]: course?.courseCategory,
            [EVENT_ATTRIBUTE.COURSE_TYPE]: course?.freeCourse ? 'free' : 'paid',
            [EVENT_ATTRIBUTE.COURSE_STATUS]: course?.course_status,
            [EVENT_ATTRIBUTE.COURSE_ID]: courseID,
            [EVENT_ATTRIBUTE.SOURCE_SCREEN]: screen,
          });
        } catch (error) {
          /* empty */
        }

        console.log(`/course-content/${courseID}-${languageID}/${course?.slug}`);

        if (course?.todays_scheduled_liveClass) {
          router.push(`/course-content/${courseID}-${languageID}/live-class`);
        } else if (newId && newSlug) {
          router.push(`/course-content/${courseID}-${languageID}/${newId}/${newSlug}`);
        } else {
          router.push(`/course-content/${courseID}-${languageID}/${course?.slug}`);
        }
      } else {
        const isExtendValidity = course.totalEmi === course.totalPaidEmi;

        if (course?.freeCourse === 1) {
          window.localStorage.setItem('routeState', JSON.stringify({ courseType, from: screen }));

          window.location.href = `/free-course/${course?.slug}`;
        } else {
          window.localStorage.setItem(
            'routeState',
            JSON.stringify({
              courseID,
              languageID,
              actionFromLibrary: 1,
              isExtendValidity,
              course_type: courseType === 'Live Classes' ? 'LIVE_CLASSES' : courseType,
              from: screen,
              course_name: course?.courseTitle,
            }),
          );

          window.location.href = `/course-intro/${course?.slug}`;
        }
      }
    }
    return true;
  };

  if (!course) return null;

  return (
    <div
      className={styles.card}
      key={course?.courseTypeId}
      id={index === 0 && 'firstCardDetails'}
      onClick={startCourse}
    >
      <div
        className={classNames(
          styles.courseItem,
          { [styles.expired]: isExpired, [styles.isExpiring]: isExpiring },
          classNameValue,
        )}
      >
        <div className={styles.courseItemThumbnail}>
          {course.thumbnail && (
            <Image
              src={course.thumbnail}
              width={140}
              height={140}
              alt={course.courseTitle}
              placeholder="blur"
              blurDataURL={course.thumbnail}
            />
          )}
        </div>
        <div className={styles.courseItemContentWrapper}>
          <div className={styles.courseItemDetails}>
            <span className={styles.courseItemTitle}>{course?.courseTitle}</span>
            {course?.liveClassActive > 0 && course?.liveClassControls > 0 && !isExpired && (
              <span className={styles.libraryCourseLive}>
                <div className={styles.liveIconButton}>
                  <span className={styles.liveIcon}>
                    <Image src={liveClassIcon} alt="Live" height={12} width={12} />
                  </span>

                  <span className={styles.liveIconText}>Live</span>
                </div>
              </span>
              // eslint-disable-next-line indent
            )}
            {/* <span className={styles.courseItemTitle}>
              {course?.referenceCount || 10} Mock{' '}
              {course?.referenceCount > 1 ? 'tests' : 'test'}
            </span> */}
            {isExpiring === true && course?.validityDaysLeft > 0 && (
              <span className={styles.expireInCourse}>
                Expires in <b>{course?.validityDaysLeft} days</b>
              </span>
            )}

            {course?.freeCourse === 1 && <span className={styles.freeCoursebadge}>Free</span>}

            {isExpiring === true && course?.validityDaysLeft === 0 && (
              <span className={styles.expireInCourse}>
                Expires <b>Today</b>
              </span>
            )}
            {repayEmi && course?.freeCourse === 0 && (
              <span className={classNames(styles.actionButton, styles.courseItemInfoValidity, styles.valididityDate)}>
                Valid Upto: <b>{courseExpireDate(course?.validityDate)}</b>
              </span>
            )}
          </div>
          <div className={styles.courseItemMoreInfo}>
            <BottomActionComponent
              course={course}
              isExpiring={isExpiring}
              isExpired={isExpired}
              repayEmi={repayEmi}
              paymentMethods={paymentMethods}
            />
          </div>
        </div>
      </div>

      <NotificationBell
        courseId={course.courseId}
        isAllowed={course?.isNotificationAllowed}
        isEnabled={course?.isNotificationEnabled}
        from="my-purchases"
      />
    </div>
  );
}

function CourseItem({ course, paymentMethods, index }) {
  const router = useRouter();
  const dispatch = useDispatch();

  // eslint-disable-next-line no-unused-vars
  const [showEMI, setShowEMI] = React.useState(false);
  const [selectedPaymentGateway, setPaymentGateway] = React.useState('');

  const isExpired = course?.freeCourse === 0 && course?.validityDaysLeft < 0;
  const isExpiring = course?.freeCourse === 0 && course?.validityDaysLeft <= 15 && course?.validityDaysLeft >= 0;
  const repayEmi =
    course?.freeCourse === 0 &&
    course?.validityDaysLeft <= 15 &&
    course?.paymentType === 2 &&
    course?.totalPaidEmi !== course?.totalEmi;
  const classes = [styles.card];

  if (course?.freeCourse) {
    classes.push('is-free');
  } else if (isExpired) {
    classes.push('expired');
  } else if (repayEmi) {
    classes.push('emi-pending');
  } else if (isExpiring) {
    classes.push('expiring');
  }

  let course_status = 'fully_paid';
  if (!course.freeCourse) {
    if (course.paymentType === 1) {
      course_status = 'renew';
    } else if (course.paymentType === 2) {
      course_status = 'EMI';
    }
  }

  const canStartCourse = (course, event) => {
    if (!event.target.closest(`.${styles.card}`)) {
      return false;
    }

    // Is Expired?
    if (isExpired) {
      router.push(`/course-detail/${course?.slug}`);

      return false;
    }

    return true;
  };

  const {
    authUser,
    paymentData,
    paytmData,
    paytmUrl,
    paymentProcessor,
    paymentProcess,
    courseOrderNumber,
    courseAmount,
    paymentProcessStarted,
    isPayTmProcessStarted,
    emiModal,
    paymentModal,
    selectedEMI,
    courseId,
  } = useSelector((state) => ({
    authUser: state.auth.authUser,
    courseOrderNumber: state.library.courseOrderNumber,
    courseAmount: state.library.courseAmount,
    paymentProcessor: state.checkout.paymentProcess.processor,
    paymentProcess: state.checkout.paymentProcess.detail,
    paymentData: state.cart.paymentData,
    paymentProcessStarted: state.cart.paymentProcessStarted,
    isPayTmProcessStarted: state.cart.isPayTmProcessStarted,
    paytmData: state.cart.paytmData,
    paytmUrl: state.cart.paytmUrl,
    emiModal: state.library.emiModal?.show,
    courseId: state.library.emiModal?.courseId,
    paymentModal: state.library.paymentModal,
    selectedEMI: state.library.selectedEMI,
  }));

  const [isRazorpayReady, razorpay] = useRazorpay({
    prefill: {
      name: authUser?.firstName,
      email: authUser?.email,
      contact: authUser?.mobileNumber,
    },
    notes: {
      orderId: course.courseTitle,
      isEmiPayment: 'YES',
    },
    theme: {
      color: '#423dcf',
    },
    modal: {
      ondismiss() {
        setPaymentGateway('');
        dispatch(resetPaymentProcess());
      },
    },
  });

  React.useEffect(() => {
    paymentProcessStarted &&
      paymentData &&
      selectedPaymentGateway === paymentMethods.CCAVENUE &&
      CCAvenuePaymentService.startTransaction(null, paymentData);
  }, [paymentProcessStarted, paymentData, selectedPaymentGateway]);

  React.useEffect(() => {
    isPayTmProcessStarted &&
      paytmData &&
      selectedPaymentGateway === paymentMethods.PAYTM &&
      PaymentActions.initiatePaytmTransaction(paytmData, paytmUrl);
  }, [isPayTmProcessStarted, paytmData, paytmUrl, selectedPaymentGateway]);

  React.useEffect(() => {
    if (
      isRazorpayReady &&
      paymentProcess &&
      paymentProcessor === 'RAZORPAY' &&
      selectedPaymentGateway === paymentMethods.RAZORPAY
    ) {
      const razorpayInstance = razorpay({
        key: paymentProcess.razorPayApiKey,
        amount: paymentProcess.amount,
        currency: paymentProcess.currency,
        order_id: paymentProcess.id,
        handler() {
          window.location.href = `/payment-response?order_id=${paymentProcess.orderId}`;
        },
      });

      razorpayInstance.startTransaction();
    }
  }, [isRazorpayReady, paymentProcess, selectedPaymentGateway]);

  React.useEffect(() => {
    if (
      isRazorpayReady &&
      courseOrderNumber &&
      courseAmount &&
      selectedPaymentGateway === paymentMethods.RAZORPAY
    ) {
      dispatch(
        createRazorpayOrder({
          orderId: courseOrderNumber,
          orderPrice: courseAmount,
        }),
      );
    }
  }, [dispatch, isRazorpayReady, courseAmount, courseOrderNumber, selectedPaymentGateway]);

  React.useEffect(() => {
    const userID = cookies.get('userID');
    const paymentAction = selectedEMI?.offer_status === 1 ? 1 : 0;
    if (userID && course && selectedPaymentGateway) {
      dispatch(
        generateCourseEMIOrder({
          courseID: course?.courseId,
          languageID: course?.lanugageId,
          emiID: selectedEMI?.orderCourseEmiId,
          amount: selectedEMI?.emiAmount,
          action: paymentAction,
          selectedPaymentStream: selectedPaymentGateway,
        }),
      );
    }
  }, [selectedPaymentGateway, dispatch]);

  React.useEffect(() => {
    const userID = cookies.get('userID');
    if (courseOrderNumber && courseAmount && selectedPaymentGateway) {
      const action = 1;
      selectedPaymentGateway === paymentMethods.CCAVENUE &&
        dispatch(
          startPaymentProcess({
            orderID: courseOrderNumber,
            orderPrice: courseAmount,
            action,
          }),
        );

      selectedPaymentGateway === paymentMethods.PAYTM &&
        userID &&
        dispatch(startPaytmPaymentProcess({ orderID: courseOrderNumber, action }));
    }
  }, [courseOrderNumber, courseAmount, dispatch]);

  const props = {
    course: {
      ...course,
      course_status,
    },
    isExpiring,
    isExpired,
    repayEmi,
    paymentMethods,
    index,
    className: classes.join(' '),
    canStartCourse,
  };

  // if (courseType === 'TEST_SERIES') {
  //   return <TestSeriesCourse {...props} />;
  // }

  return (
    <>
      <VideoCourse {...props} />
      {emiModal && courseId === course?.courseId && (
        <CourseEMIModal
          course={course}
          setShow={setShowEMI}
          paymentMethods={paymentMethods}
          showEMI
        />
      )}
      {paymentModal && courseId === course?.courseId && (
        <PaymentModal
          paymentMethods={paymentMethods}
          setPaymentGateway={setPaymentGateway}
          setIsPaymentInitialized={() => setShowEMI(false)}
        />
      )}
    </>
  );
}

export default CourseItem;
